import { useComputed } from "@suse-o11y/signal";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { selectAllSearchParams } from "../../app/state/route/RouteSelectors";
import { useCurrentTraversalData } from "../../app/state/view/query/TopologyQueryHooks";
import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { LAST_6_HOURS, getStaticTimeRange } from "../../app/state/view/timeline/TimeRange";
import { useCurrentViewQuery } from "../../app/state/view/viewpoint/ViewPointHooks";
import type { FullComponent } from "../../common/openapi";
import type { TraversalData } from "../../components/react/topology-query/TopologyQueryFunctions";
import { useTimestampCursorState } from "../../components/TimestampCursor";
import { matchPath } from "../../generic/routing";
import { queryFullComponent } from "../../queries";
import { useQuery } from "../../queries/react-query";
import { queryUIConfig } from "../../queries/ui-config";
import { COMPONENT_PATH, VIEW_PATH } from "../../routes/Routes";
import { adjustTimeRangeAndTimestamp } from "../../routes/timeline/TimelineQueryUtils";
import type { BorgContextPin } from "../models/borg-types";

import type { AiAssistantInput } from "./build-question";

/** Extract the component identifier from the drawer input, if available. */
const getComponentIdentifier = (input: AiAssistantInput): string | undefined =>
	input._kind === "chat" ? input.componentIdentifier : undefined;

/** Topology time pin: determines which components exist and their health state. */
const buildTopologyTimePin = (timeTravelDate: Date | undefined): BorgContextPin => ({
	type: "topology_time",
	is_live: timeTravelDate == null,
	point_ms: timeTravelDate?.getTime(),
});

const getRangeEndMs = (timeRange: TimeRange): number | undefined => timeRange.end?.getTime();

/** Telemetry interval pin: the visible time window for events, logs, metrics, traces. */
const buildTelemetryIntervalPin = (
	timeRange: TimeRange,
	markers: ReadonlyArray<{ timestamp: number }>,
): BorgContextPin => ({
	type: "telemetry_interval",
	start_ms: timeRange.start().getTime(),
	end_ms: getRangeEndMs(timeRange),
	range_label: timeRange.toUserFriendlyString(),
	markers_ms: markers.length > 0 ? markers.map((m) => m.timestamp) : undefined,
});

/** Scope/filter pins extracted from the current STQL traversal data. */
const FILTER_DOMAINS = ["domain", "layer", "type", "healthstate"] as const;

/** Parse a label filter string (e.g. "namespace:opentelemetry-demo") into a filter pin. */
const parseLabelPin = (label: string): BorgContextPin => {
	const colonIdx = label.indexOf(":");
	if (colonIdx <= 0) {
		return { type: "filter", name: "label", value: label };
	}
	const rawName = label.slice(0, colonIdx);
	const name = rawName === "cluster-name" ? "cluster" : rawName;
	return { type: "filter", name, value: label.slice(colonIdx + 1) };
};

const buildScopeFilterPins = (traversal: TraversalData | undefined): Array<BorgContextPin> => {
	if (traversal == null) {
		return [];
	}

	const pins: Array<BorgContextPin> = [];

	for (const domain of FILTER_DOMAINS) {
		const values = traversal.getBasicFiltersFor(domain);
		if (values != null) {
			for (const value of values) {
				pins.push({ type: "filter", name: domain, value });
			}
		}
	}

	const labels = traversal.getBasicFiltersFor("label");
	if (labels != null) {
		for (const label of labels) {
			pins.push(parseLabelPin(label));
		}
	}

	return pins;
};

const buildComponentPin = (component: FullComponent): BorgContextPin => ({
	type: "component",
	name: component.data.name,
	component_type: component.typeName,
	urn: component.data.identifiers?.[0] ?? undefined,
	health_state: component.data.health ?? undefined,
});

export const useContextPins = (input: AiAssistantInput): ReadonlyArray<BorgContextPin> => {
	const location = useLocation();
	const { timestamp, timeRange: selectedTimeRange } = useSelector(selectAllSearchParams);
	const { data: defaultTimeRange = LAST_6_HOURS } = useQuery({
		...queryUIConfig,
		select: ({ webUIConfig }) => getStaticTimeRange(webUIConfig.defaultTimeRange) ?? LAST_6_HOURS,
	});

	const { timeTravelDate, timeRange, viewMode } = useMemo(() => {
		const adjustedTimeline = adjustTimeRangeAndTimestamp({
			timestamp,
			timeRange: selectedTimeRange ?? defaultTimeRange,
		});
		const componentMatch = matchPath(location.pathname, COMPONENT_PATH);
		const viewMatch = matchPath(location.pathname, VIEW_PATH);

		return {
			timeTravelDate: adjustedTimeline.timestamp,
			timeRange: adjustedTimeline.timeRange,
			viewMode: componentMatch?.params.mode ?? viewMatch?.params.mode,
		};
	}, [defaultTimeRange, location, selectedTimeRange, timestamp]);
	const viewQuery = useCurrentViewQuery().getOrElse("");
	const traversal = useCurrentTraversalData();

	const timestampCursorState = useTimestampCursorState();
	const markers = useComputed((monitor) => timestampCursorState.getAllMarkers(monitor), [timestampCursorState]);

	const componentIdentifier = getComponentIdentifier(input);
	const { data: component } = useQuery({
		enabled: componentIdentifier != null,
		...queryFullComponent({ componentIdOrIdentifier: componentIdentifier ?? "", timestamp }),
	});

	return useMemo(() => {
		const pins: Array<BorgContextPin> = [];

		pins.push(buildTopologyTimePin(timeTravelDate));

		if (timeRange != null) {
			pins.push(buildTelemetryIntervalPin(timeRange, markers));
		}

		if (component != null) {
			pins.push(buildComponentPin(component));
		}

		if (viewQuery.length > 0) {
			pins.push({ type: "query", query: viewQuery });
		}

		pins.push(...buildScopeFilterPins(traversal));

		if (viewMode != null) {
			pins.push({ type: "filter", name: "perspective", value: viewMode });
		}

		return pins;
	}, [timeTravelDate, timeRange, component, viewQuery, markers, traversal, viewMode]);
};
